import { type PlatformErrorAreas, runWithErrorReporting } from "$root/api/error-reporting";
import { platformToast, ToastContent } from "$root/notification-system/toast";
import { ToastableError } from "$root/utils";
import { Column, Text } from "@mdotm/shadcn/legacy/components";
import { noop } from "@mdotm/shadcn/legacy/utils";
import {
	BulkActionReviewSections,
	checkBulkActionAvailability,
	spawnBulkActionReviewDialog,
	type BulkActionValidator,
	type ReasonToMessageMap,
	type ReviewSectionLabels,
} from "./common";

export type SpawnBulkExportActionParams<Entity extends { uuid?: string; name?: string }, Reason extends string> = {
	reasonKeys: readonly Reason[];
	validator: BulkActionValidator<Entity, Reason>;
	entities: Entity[];
	reasonToMessage: ReasonToMessageMap<Reason>;
	labels: ReviewSectionLabels;
	getRedirectUrl: (entityUuids: string[]) => string;
	area: PlatformErrorAreas;
	/** `persist=false` downloads the file now; `persist=true` builds it in the background. */
	downloadBulkTemplate: (uuids: string[], persist: boolean) => Promise<void>;
	/** Called once the export is handed over: at once for a background export, after the download otherwise. */
	onStarted: () => void;
	hideRedirectUrl?: boolean;
	showEntityNames?: boolean;
};

function toastBackgroundDownload() {
	platformToast({
		children: (
			<ToastContent alignIcon="top">
				<Column gap={8} flexGrow={0}>
					<div>
						<Text as="div" type="Body/L/Bold">
							Download
						</Text>
						<div>Sphere has taken over your request</div>
					</div>
				</Column>
			</ToastContent>
		),
		icon: "Portfolio",
		severity: "success",
	});
}

export async function spawnBulkExportAction<Entity extends { uuid?: string; name?: string }, Reason extends string>(
	params: SpawnBulkExportActionParams<Entity, Reason>,
): Promise<void> {
	const {
		reasonKeys,
		validator,
		entities,
		reasonToMessage,
		labels,
		getRedirectUrl,
		area,
		downloadBulkTemplate,
		onStarted,
		hideRedirectUrl,
		showEntityNames,
	} = params;

	const availabilityResult = checkBulkActionAvailability(reasonKeys, validator, entities);
	const [available, unavailable] = availabilityResult;

	async function onContinue() {
		const uuids = available.flatMap(({ uuid }) => (uuid ? [uuid] : []));
		const persist = uuids.length > 1;
		const exportTemplate = () =>
			runWithErrorReporting(
				() =>
					downloadBulkTemplate(uuids, persist).catch((error: unknown) => {
						throw new ToastableError(`Unable to export ${labels.entityPlural.toLowerCase()}`, {
							cause: error,
							icon: "Portfolio",
						});
					}),
				{
					area,
					attemptedOperation: {
						message: "export the selection to one bulk template",
						payload: JSON.stringify({ uuids, persist }),
					},
				},
			);

		if (persist) {
			toastBackgroundDownload();
			onStarted();
			void exportTemplate().catch(noop);
			return;
		}
		await exportTemplate();
		onStarted();
	}

	if (!unavailable) {
		await onContinue();
		return;
	}

	spawnBulkActionReviewDialog({
		initialState: null,
		header: "Download: review and confirm",
		children: (
			<BulkActionReviewSections
				availabilityResult={availabilityResult}
				reasonToMessage={reasonToMessage}
				getRedirectUrl={getRedirectUrl}
				hideRedirectUrl={hideRedirectUrl}
				showEntityNames={showEntityNames}
				labels={labels}
			/>
		),
		canConfirm: available.length > 0,
		onContinue,
	}).catch(noop);
}
